import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { collection, doc, getDoc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert
} from '@mui/material';

function OfferDetail() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = offerId === 'new';

  const [offer, setOffer] = useState({
    title: '',
    advertiser: { name: '', contactEmail: '', logoUrl: '' },
    product: { name: '', description: '', sku: '', msrp: '', detailUrl: '' },
    discount: { type: 'percentage', value: 0, expiresAt: null },
    assets: { imageUrl: '', bannerUrl: '', thumbnailUrl: '' },
    published: false,
    memberOffer: false,
    priority: 0
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previews, setPreviews] = useState({ image: '', banner: '', thumbnail: '' });

  useEffect(() => {
    if (!isNew && offerId) {
      const load = async () => {
        setLoading(true);
        try {
          const docRef = doc(db, 'offers', offerId);
          const snap = await getDoc(docRef);
          if (snap.exists()) {
            const data = snap.data();
            // Convert Firestore Timestamp to input-friendly string for expiresAt
            const normalized = { ...data };
            if (data.discount) {
                if (data.discount.expiresAt && data.discount.expiresAt.seconds) {
                normalized.discount.expiresAt = new Date(data.discount.expiresAt.seconds * 1000).toISOString().slice(0,16);
                } else if (data.discount.expiresAt instanceof Date) {
                normalized.discount.expiresAt = data.discount.expiresAt.toISOString().slice(0,16);
                } else if (!data.discount.expiresAt) {
                normalized.discount.expiresAt = '';
                }
            }
            setOffer({ id: snap.id, ...normalized });
          }
          else setError('Offer not found');
        } catch (err) {
          console.error('Error loading offer:', err);
          setError('Failed to load offer');
        } finally {
          setLoading(false);
        }
      };
      load();
    }
  }, [isNew, offerId]);

  // Build image previews from storage paths or external URLs
  useEffect(() => {
    let mounted = true;
    const buildPreviews = async () => {
      const assets = offer.assets || {};
      const result = { image: '', banner: '', thumbnail: '' };

      const loadOne = async (val) => {
        if (!val) return '';
        // storage path pattern used earlier: offers/{offerId}/file
        if (typeof val === 'string' && val.startsWith('offers/')) {
          try {
            const url = await getDownloadURL(ref(storage, val));
            return url;
          } catch (e) {
            return '';
          }
        }
        // external URL
        if (typeof val === 'string' && (val.startsWith('http://') || val.startsWith('https://'))) {
          return val;
        }
        return '';
      };

      result.image = await loadOne(assets.imageUrl || assets.image);
      result.banner = await loadOne(assets.bannerUrl || assets.banner);
      result.thumbnail = await loadOne(assets.thumbnailUrl || assets.thumbnail);

      if (mounted) setPreviews(result);
    };

    buildPreviews();
    return () => { mounted = false; };
  }, [offer.assets]);

  const toDatetimeLocal = (v) => {
    if (!v) return '';
    // string in correct format
    if (typeof v === 'string') {
      if (v.includes('T')) return v.slice(0,16);
      const parsed = new Date(v);
      if (!isNaN(parsed)) return parsed.toISOString().slice(0,16);
      return '';
    }
    // Firestore Timestamp
    if (v && typeof v === 'object' && v.seconds) {
      return new Date(v.seconds * 1000).toISOString().slice(0,16);
    }
    if (v instanceof Date) return v.toISOString().slice(0,16);
    return '';
  };


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('advertiser.')) {
      const key = name.split('.')[1];
      setOffer(prev => ({ ...prev, advertiser: { ...prev.advertiser, [key]: value } }));
    } else if (name.startsWith('product.')) {
      const key = name.split('.')[1];
      setOffer(prev => ({ ...prev, product: { ...prev.product, [key]: value } }));
    } else if (name.startsWith('assets.')) {
      const key = name.split('.')[1];
      setOffer(prev => ({ ...prev, assets: { ...(prev.assets || {}), [key]: value } }));
    } else if (name.startsWith('discount.')) {
      const key = name.split('.')[1];
      setOffer(prev => ({ ...prev, discount: { ...prev.discount, [key]: value } }));
    } else if (name === 'published' || name === 'memberOffer') {
      setOffer(prev => ({ ...prev, [name]: checked }));
    } else {
      setOffer(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Validate discount expiration (if provided must be a future date)
      if (offer.discount?.expiresAt) {
        const d = new Date(offer.discount.expiresAt);
        if (isNaN(d.getTime())) {
          setError('Invalid discount expiration date');
          setSaving(false);
          return;
        }
        const now = new Date();
        if (d <= now) {
          setError('Discount expiration must be a future date');
          setSaving(false);
          return;
        }
      }

      // Prepare payload, convert discount.expiresAt (ISO string) to Date for Firestore
      const payload = { ...offer };
      // Remove local id if present
      delete payload.id;
      payload.discount.expiresAt = payload.discount.expiresAt ? new Date(payload.discount.expiresAt) : null;

      if (isNew) {
        const data = { ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
        const colRef = collection(db, 'offers');
        const docRef = await addDoc(colRef, data);
        // After saving, go back to the offers list
        navigate('/offers');
      } else {
        const docRef = doc(db, 'offers', offerId);
        await updateDoc(docRef, { ...payload, updatedAt: serverTimestamp() });
        // After saving, go back to the offers list
        navigate('/offers');
      }
      setError(null);
    } catch (err) {
      console.error('Error saving offer:', err);
      setError('Failed to save offer');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file, imageType) => {
    if (isNew) {
      setError('Save the offer first before uploading images');
      return;
    }
    if (!file) return;
    const ext = file.name.split('.').pop();
    const fileName = `${imageType}.${ext}`;
    const path = `offers/${offerId}/${fileName}`;
    const storageRef = ref(storage, path);
    try {
      // delete old if exists
      const prevPath = offer.assets?.[`${imageType}Url`];
      if (prevPath) {
        try { await deleteObject(ref(storage, prevPath)); } catch (e) { /* ignore */ }
      }
      await uploadBytes(storageRef, file);
      // store storage path
      const update = { assets: { ...(offer.assets || {}), [`${imageType}Url`]: path } };
      const docRef = doc(db, 'offers', offerId);
      await updateDoc(docRef, update);
      setOffer(prev => ({ ...prev, assets: { ...(prev.assets || {}), [`${imageType}Url`]: path } }));
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>
  );

  return (
    <Box sx={{ p: 3 }} component="form" onSubmit={handleSave}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{isNew ? 'New Offer' : `Edit Offer - ${offer.title || ''}`}</Typography>
        <Box>
          <Button sx={{ mr: 1 }} onClick={() => navigate('/offers')}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <TextField fullWidth label="Title" name="title" value={offer.title || ''} onChange={handleChange} sx={{ mb: 2 }} />

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Product</Typography>
          <TextField fullWidth label="Name" name="product.name" value={offer.product?.name || ''} onChange={handleChange} sx={{ mb: 2 }} />
          <TextField fullWidth multiline label="Description" name="product.description" value={offer.product?.description || ''} onChange={handleChange} sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}><TextField fullWidth label="SKU" name="product.sku" value={offer.product?.sku || ''} onChange={handleChange} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="MSRP" name="product.msrp" value={offer.product?.msrp || ''} onChange={handleChange} /></Grid>
          </Grid>
          <TextField fullWidth label="Product Page URL" name="product.detailUrl" value={offer.product?.detailUrl || ''} onChange={handleChange} sx={{ mb: 2 }} />

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Discount</Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="discount-type-label">Type</InputLabel>
            <Select labelId="discount-type-label" label="Type" name="discount.type" value={offer.discount?.type || 'percentage'} onChange={handleChange}>
              <MenuItem value="percentage">Percentage</MenuItem>
              <MenuItem value="fixed-amount">Fixed Amount</MenuItem>
            </Select>
          </FormControl>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}><TextField fullWidth label="Value" name="discount.value" value={offer.discount?.value || 0} onChange={handleChange} /></Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Offer Expires At"
                name="discount.expiresAt"
                InputLabelProps={{ shrink: true }}
                value={toDatetimeLocal(offer.discount?.expiresAt)}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Advertiser</Typography>
            <TextField fullWidth label="Name" name="advertiser.name" value={offer.advertiser?.name || ''} onChange={handleChange} sx={{ mt: 1 }} />
            <TextField fullWidth label="Contact Email" name="advertiser.contactEmail" value={offer.advertiser?.contactEmail || ''} onChange={handleChange} sx={{ mt: 1 }} />

            <Typography variant="subtitle2" sx={{ mt: 2 }}>Images</Typography>
            <Typography variant="caption" display="block" sx={{ mb: 1 }}>Main image</Typography>
            {offer.assets?.imageUrl && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">Stored: {offer.assets.imageUrl}</Typography>
                {previews.image ? (
                  <Box component="img" src={previews.image} alt="main" sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', mt: 1, borderRadius: 1 }} />
                ) : null}
              </Box>
            )}
            <Button variant="outlined" component="label" fullWidth sx={{ mb: 1 }}>
              Upload Main Image
              <input hidden type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], 'image')} />
            </Button>
            <TextField fullWidth label="Or image URL" name="assets.imageUrl" value={offer.assets?.imageUrl || ''} onChange={handleChange} sx={{ mb: 1 }} />

            <Typography variant="caption" display="block" sx={{ mb: 1 }}>Banner image</Typography>
            {offer.assets?.bannerUrl && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">Stored: {offer.assets.bannerUrl}</Typography>
                {previews.banner ? (
                  <Box component="img" src={previews.banner} alt="banner" sx={{ width: '100%', maxHeight: 120, objectFit: 'cover', mt: 1, borderRadius: 1 }} />
                ) : null}
              </Box>
            )}
            <Button variant="outlined" component="label" fullWidth sx={{ mb: 1 }}>
              Upload Banner
              <input hidden type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], 'banner')} />
            </Button>
            <TextField fullWidth label="Or banner URL" name="assets.bannerUrl" value={offer.assets?.bannerUrl || ''} onChange={handleChange} sx={{ mb: 1 }} />

            <Typography variant="caption" display="block" sx={{ mb: 1 }}>Thumbnail</Typography>
            {offer.assets?.thumbnailUrl && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">Stored: {offer.assets.thumbnailUrl}</Typography>
                {previews.thumbnail ? (
                  <Box component="img" src={previews.thumbnail} alt="thumbnail" sx={{ width: 120, height: 120, objectFit: 'cover', mt: 1, borderRadius: 1 }} />
                ) : null}
              </Box>
            )}
            <Button variant="outlined" component="label" fullWidth sx={{ mb: 1 }}>
              Upload Thumbnail
              <input hidden type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], 'thumbnail')} />
            </Button>
            <TextField fullWidth label="Or thumbnail URL" name="assets.thumbnailUrl" value={offer.assets?.thumbnailUrl || ''} onChange={handleChange} sx={{ mb: 1 }} />

            <FormControlLabel control={<Switch checked={offer.memberOffer || false} onChange={handleChange} name="memberOffer" />} label="Member Offer" sx={{ mt: 1 }} />
            <FormControlLabel control={<Switch checked={offer.published || false} onChange={handleChange} name="published" />} label="Published" sx={{ mt: 2 }} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default OfferDetail;
